/**
 * SearchInput Component
 * Reusable search input with icon and consistent styling
 */

import { InputHTMLAttributes, forwardRef } from 'react';
import { Search, X } from 'lucide-react';

interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    onClear?: () => void;
    showClearButton?: boolean;
    containerClassName?: string;
    /** Accessible label for screen readers. Defaults to placeholder if not provided */
    'aria-label'?: string;
}

/**
 * SearchInput - A reusable search input component
 *
 * @example
 * // Basic usage
 * <SearchInput
 *   placeholder="Search contacts..."
 *   value={searchTerm}
 *   onChange={e => setSearchTerm(e.target.value)}
 * />
 *
 * // With clear button
 * <SearchInput
 *   placeholder="Search..."
 *   value={searchTerm}
 *   onChange={e => setSearchTerm(e.target.value)}
 *   onClear={() => setSearchTerm('')}
 *   showClearButton={searchTerm.length > 0}
 * />
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
    ({
        className = '',
        containerClassName = '',
        onClear,
        showClearButton = false,
        value,
        placeholder,
        'aria-label': ariaLabel,
        ...props
    }, ref) => {
        const hasValue = value && String(value).length > 0;

        return (
            <div className={`relative ${containerClassName}`}>
                <Search
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                    ref={ref}
                    type="text"
                    value={value}
                    placeholder={placeholder}
                    aria-label={ariaLabel || placeholder || 'Search'}
                    className={`
                        w-full pl-10 pr-${showClearButton && hasValue ? '10' : '4'} py-2
                        border border-gray-300 rounded-lg
                        focus:ring-2 focus:ring-primary focus:border-transparent
                        transition-colors
                        ${className}
                    `.trim().replace(/\s+/g, ' ')}
                    {...props}
                />
                {showClearButton && hasValue && onClear && (
                    <button
                        type="button"
                        onClick={onClear}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Clear search"
                    >
                        <X size={18} />
                    </button>
                )}
            </div>
        );
    }
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
